import { MiddlewareComposition } from './middleware';
import { workAroundPopUpBlockers } from './utils/workarounds';
export function getDefaultHyperlinkWatcherOptions() {
    return {
        selector: 'a:not([data-skip-middlewares])',
        composition: new MiddlewareComposition(),
        disablePopUpBlockersWorkaround: false,
    };
}
/** Its instance allows you to chain middlewares */
export class HyperlinkWatcher {
    constructor(options) {
        this.observer = null;
        /** With stop the default click to simulate our how logic with middlewares
         *
         * Note: the structure of this method is a bit different because of a potentiel issue when binding the context (ref: https://github.com/typescript-eslint/typescript-eslint/blob/main/packages/eslint-plugin/docs/rules/unbound-method.md)
         */
        this.elementClicked = (event) => {
            // Only act if the underlying corresponds to a valid selector we know how to manage
            // Note: we use `currentTarget` because it will always represent the hyperlink, whereas `target` can be the nested element being clicked (in the hyperlink content)
            if (event.currentTarget) {
                const triggeredHyperlink = event.currentTarget;
                if (triggeredHyperlink.nodeName === 'A') {
                    const hyperlinkElement = triggeredHyperlink;
                    const finalProperties = this.options.composition.apply({
                        href: hyperlinkElement.href,
                        target: hyperlinkElement.target,
                    }, hyperlinkElement);
                    // We handle on our own the hyperlink action only if there was a change made inside middlewares
                    if (hyperlinkElement.href !== finalProperties.href ||
                        hyperlinkElement.target !== finalProperties.target) {
                        event.preventDefault();
                        // Default to `_self` otherwise when not specified it will be `_blank`
                        const hyperlinkTarget = finalProperties.target !== '' ? finalProperties.target : '_self';
                        const newWindow = window.open(finalProperties.href, hyperlinkTarget);
                        if (newWindow &&
                            hyperlinkTarget === '_blank' &&
                            this.options.disablePopUpBlockersWorkaround !== true) {
                            workAroundPopUpBlockers(newWindow, finalProperties.href);
                        }
                    }
                }
            }
        };
        this.options = Object.assign(Object.assign({}, getDefaultHyperlinkWatcherOptions()), options);
        this.scopedElementClicked = this.elementClicked.bind(this);
    }
    /** Watch clicks on DOM hyperlinks
     *
     * Note: this will watch existing hyperlinks when called, but will also manage new hyperlinks added to the DOM
     */
    watch() {
        document.querySelectorAll(this.options.selector).forEach(element => {
            this.watchElementClick(element);
        });
        // Watch the ones that could be added after
        const targetNode = document.body; // We watch in all cases from the top element, and we will filter below (no other choice due to flexible selector option)
        const config = { attributes: false, childList: true, subtree: true };
        const callback = (mutationsList) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach(addedNode => {
                        // Check it's an element
                        if (addedNode.nodeType === Node.ELEMENT_NODE) {
                            const addedElement = addedNode;
                            // If the added node is concerned
                            if (addedElement.matches(this.options.selector)) {
                                this.watchElementClick(addedElement);
                            }
                            // In case the added node has children, we parse all of them because a match can be inside
                            addedElement
                                .querySelectorAll(this.options.selector)
                                .forEach(element => {
                                this.watchElementClick(element);
                            });
                        }
                    });
                }
            }
        };
        this.observer = new MutationObserver(callback);
        this.observer.observe(targetNode, config);
    }
    /** Stop watching clicks on DOM hyperlinks (useful to free memory) */
    unwatch() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        document.querySelectorAll(this.options.selector).forEach(element => {
            element.removeEventListener('click', this.scopedElementClicked, false);
        });
    }
    /** Helper to add listener since called at different places */
    watchElementClick(element) {
        element.addEventListener('click', this.scopedElementClicked, false);
    }
}
//# sourceMappingURL=watcher.js.map